import { Chip, Collapse, Icon, IconButton, Paper, Typography } from '@shared/ui';
import { useState } from 'react';
import { registerWidgetRenderer } from '../registry';
import type { DebatePersonaEntity, TimelineWidgetProps } from '../types';

export interface DebatePersonaWidgetProps extends TimelineWidgetProps<DebatePersonaEntity> {
  debugMode?: boolean;
}

registerWidgetRenderer(
  'debate_persona',
  ({ entity, debugMode }) => (
    <DebatePersonaWidget key={(entity as any).id} entity={entity as any} debugMode={debugMode} />
  ),
  { visibility: { normal: true, debug: true } }
);

export function DebatePersonaWidget({ entity, debugMode = false }: DebatePersonaWidgetProps) {
  const { status, name, role, perspective, personality_traits, bio, is_personification } =
    entity.props;
  const [expanded, setExpanded] = useState(true);

  const statusColor = status === 'completed' ? 'success' : status === 'error' ? 'error' : 'primary';

  return (
    <Paper variant="outlined" className="p-4 bg-gradient-to-r from-red-50 to-orange-50">
      <div className="flex items-start gap-3">
        <Icon sx={{ fontSize: 24, color: `${statusColor}.main` }}>
          {is_personification ? 'lightbulb' : 'person'}
        </Icon>

        <div className="flex-1">
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => setExpanded(!expanded)}
          >
            <Typography variant="subtitle1" fontWeight="bold">
              {is_personification ? '🎭' : '👤'} {name || 'Unnamed Persona'}
            </Typography>
            {role ? <Chip label={role} size="small" color="primary" variant="outlined" /> : null}
            {is_personification ? (
              <Chip label="Personification" size="small" color="secondary" variant="outlined" />
            ) : null}
            <IconButton size="small" sx={{ ml: 'auto' }}>
              <Icon sx={{ fontSize: 18 }}>{expanded ? 'expand_less' : 'expand_more'}</Icon>
            </IconButton>
          </div>

          <Collapse in={expanded}>
            {status === 'active' || status === 'completed' ? (
              <div className="mt-3 space-y-3">
                {perspective ? (
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <Typography variant="subtitle2" className="mb-1">
                      Perspective
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {perspective}
                    </Typography>
                  </div>
                ) : null}

                {personality_traits && personality_traits.length > 0 ? (
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <Typography variant="subtitle2" className="mb-2">
                      Personality Traits
                    </Typography>
                    <div className="flex flex-wrap gap-1">
                      {personality_traits.map((trait, idx) => (
                        <Chip key={idx} label={trait} size="small" variant="outlined" />
                      ))}
                    </div>
                  </div>
                ) : null}

                {bio ? (
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <Typography variant="subtitle2" className="mb-1">
                      Bio
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {bio}
                    </Typography>
                  </div>
                ) : null}

                {status === 'completed' && entity.props.error ? (
                  <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                    <Typography variant="body2" color="error">
                      Error: {entity.props.error}
                    </Typography>
                  </div>
                ) : null}

                {debugMode ? (
                  <details className="bg-gray-100 rounded p-2">
                    <summary className="cursor-pointer text-xs font-mono">Debug Info</summary>
                    <pre className="text-xs mt-2 overflow-auto max-h-48">
                      {JSON.stringify(entity.props, null, 2)}
                    </pre>
                  </details>
                ) : null}
              </div>
            ) : null}

            {status === 'error' ? (
              <div className="mt-3">
                <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                  <Typography variant="body2" color="error">
                    {entity.props.error || 'An error occurred while processing persona'}
                  </Typography>
                </div>
              </div>
            ) : null}
          </Collapse>
        </div>
      </div>
    </Paper>
  );
}
